import { apiErrorMessage } from "@/lib/api";
import type { DependencyList } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * The read-side counterpart to `useApiMutation`. Components all over the
 * app hand-rolled the same effect ceremony for a secondary fetch (the
 * one the route loader doesn't cover — a lazy list, a dependent lookup):
 *
 *   const [data, setData] = useState<T | null>(null);
 *   const [loading, setLoading] = useState(false);
 *   useEffect(() => {
 *     let cancelled = false;
 *     setLoading(true);
 *     api.get<T>(url)
 *       .then((r) => { if (!cancelled) setData(r); })
 *       .catch(() => { if (!cancelled) setData(fallback); })
 *       .finally(() => { if (!cancelled) setLoading(false); });
 *     return () => { cancelled = true; };
 *   }, [dep]);
 *
 * `useApiQuery` owns that ceremony — the loading flag, the stale-response
 * guard (a response that resolves after its deps changed is dropped), the
 * error extraction, and a `reload()` to re-run after a mutation. The
 * fetcher runs on mount and whenever `deps` change; `enabled: false`
 * holds it back (e.g. until a dialog opens or an id is picked).
 *
 * Not a fit for realtime / debounced / polling reads (search-as-you-type,
 * socket-driven timers) — those own their own effect on purpose.
 */

export type UseApiQueryOptions<T> = {
  /** Skip the fetch while false — `data` stays at `initialData`, `loading`
   *  is false. Flips to a fetch as soon as it turns true. Default true. */
  enabled?: boolean;
  /** Seed value before the first load resolves. Default `null`. */
  initialData?: T | null;
  /** Friendly fallback for the surfaced error string (see `apiErrorMessage`). */
  fallbackMessage?: string;
  /** Runs on a settled failure — mirror of `useApiMutation`'s `onError`. */
  onError?: (message: string, err: unknown) => void;
};

export type UseApiQueryResult<T> = {
  /** The latest successful result, or `initialData` (null) before/around a load. */
  data: T | null;
  /** True while a fetch is in flight. */
  loading: boolean;
  /** Last failure's message, or `null`. Cleared at the start of each run. */
  error: string | null;
  /** Re-run the fetch imperatively — after a mutation, on a refresh button. */
  reload: () => void;
};

export function useApiQuery<T>(
  fetcher: () => Promise<T>,
  deps: DependencyList,
  options?: UseApiQueryOptions<T>,
): UseApiQueryResult<T> {
  const enabled = options?.enabled ?? true;
  const [data, setData] = useState<T | null>(options?.initialData ?? null);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  // Latest closures without re-triggering the effect on every render — it
  // keys only on `deps` (the caller's contract), `enabled`, and `nonce`.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // biome-ignore lint/correctness/useExhaustiveDependencies: `deps` is the caller's dependency contract and `nonce` is the reload trigger, not read in the body; fetcher/options ride in refs
  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetcherRef
      .current()
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        const message = apiErrorMessage(err, optionsRef.current?.fallbackMessage);
        setError(message);
        setLoading(false);
        optionsRef.current?.onError?.(message, err);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, nonce, ...deps]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { data, loading, error, reload };
}
